import styled from "styled-components";
import { useEffect, useState, useRef } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { captureException } from "@sentry/react";
import * as tus from "tus-js-client";

import { AD_PLAN_VIDEO } from "constants";
import Media from "./Media";
import LoadingBox from "components/other/LoadingBox";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import Backdrop from "components/other/Backdrop";
import MessageWindow from "components/other/MessageWindow";

const ModalWrap = styled.div`
  width: 100%;
  margin: 0;
  padding: 0;
`;

export default ({ plan }) => {
  const [errorWindow, setErrorWindow] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [showUploadButton, setShowUploadButton] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileInputKey, setFileInputKey] = useState(() => new Date().getTime());
  const [confirmDeleteMedia, setConfirmDeleteMedia] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const fileInputRef = useRef();

  // Fetch data
  const GET_MEDIA_DATA = gql`
    query GetMediaData {
      mediaData {
        id
        created_at
        video_id
        original_name
        status
      }
    }
  `;

  const { data, error, refetch } = useQuery(GET_MEDIA_DATA, {
    fetchPolicy: "network-only",
  });

  if (error) {
    console.log("Fetching media data error", error);
    captureException(error);
  }

  // Error handling
  const openErrorWindow = () => {
    setErrorWindow(true);
  };

  const handleModalClose = (modal) => {
    if (modal === "error") {
      setErrorWindow(false);
    }
  };

  // Handle file upload
  const onFileInputChange = (e) => {
    if (e.target.files[0]) setShowUploadButton(true);
  };

  const PREPARE_VIDEO_UPLOAD = gql`
    mutation PrepareVideoUpload($title: String!, $expiration: Int!) {
      prepareVideoUpload(title: $title, expiration: $expiration) {
        videoId
        signature
      }
    }
  `;

  const [prepareVideoUpload] = useMutation(PREPARE_VIDEO_UPLOAD);

  const SAVE_MEDIA_FILE = gql`
    mutation SaveMediaFile($videoId: String!, $originalFileName: String!) {
      saveMediaFile(videoId: $videoId, originalFileName: $originalFileName) {
        resultCode
      }
    }
  `;

  const [saveMediaFile] = useMutation(SAVE_MEDIA_FILE);

  const onUploadClick = async () => {
    try {
      setErrorWindow(false);
      setErrorMessage(null);

      if (plan < AD_PLAN_VIDEO) {
        setUploadErrorMessage(
          "Uploading videos is only allowed for users with a Silver or higher advertising plan."
        );

        return;
      }

      if (!fileInputRef.current.files[0]) return;

      let newWarnings = [];

      const maxFileSize = 200000;

      const fileSize = fileInputRef.current.files[0].size;

      if (fileSize > maxFileSize * 1000) {
        newWarnings.push("fileTooLarge");
      }

      if (newWarnings.length > 0) {
        setWarnings(newWarnings);
        return;
      }

      setUploadLoading(true);

      const expiration = new Date();
      expiration.setHours(expiration.getHours() + 1);

      const expirationEpoch = Math.round(expiration.getTime() / 1000);

      // prepare upload and get Video id
      const title = fileInputRef.current.files[0].name;

      const prepareUploadResult = await prepareVideoUpload({
        variables: {
          title,
          expiration: expirationEpoch,
        },
      });

      const { videoId, signature } =
        prepareUploadResult.data.prepareVideoUpload;

      if (!videoId) {
        setUploadLoading(false);
        setUploadProgress(0);
        openErrorWindow();
      }

      const file = fileInputRef.current.files[0];

      var upload = new tus.Upload(file, {
        endpoint: "https://video.bunnycdn.com/tusupload",
        retryDelays: [0, 3000, 5000, 10000, 20000, 60000, 60000],
        headers: {
          AuthorizationSignature: signature, // SHA256 signature (library_id + api_key + expiration_time + video_id)
          AuthorizationExpire: expirationEpoch, // Expiration time as in the signature,
          VideoId: videoId, // The guid of a previously created video object through the Create Video API call
          LibraryId: 394352,
        },
        metadata: {
          filetype: file.type,
          title: title,
        },
        onError: function (error) {
          console.log("Upload error", error);
          setUploadLoading(false);
          setUploadProgress(0);
          openErrorWindow();
        },
        onProgress: function (bytesUploaded) {
          let progress = Math.round((100 / fileSize) * bytesUploaded);

          if (progress > 100) progress = 100;

          setUploadProgress(progress);
        },
        onSuccess: async function () {
          const saveResult = await saveMediaFile({
            variables: {
              videoId,
              originalFileName: title,
            },
          });

          setUploadLoading(false);
          setUploadProgress(0);
          setFileInputKey(new Date().getTime());
          setShowUploadButton(false);

          if (saveResult.data.saveMediaFile.resultCode !== 0) {
            openErrorWindow();
          }

          refetch();
        },
      });

      // Check if there are any previous uploads to continue.
      upload.findPreviousUploads().then(function (previousUploads) {
        // Found previous uploads so we select the first one.
        if (previousUploads.length) {
          upload.resumeFromPreviousUpload(previousUploads[0]);
        }

        // Start the upload
        upload.start();
      });
    } catch (error) {
      captureException(error);
      console.log("onUploadClick error", error);
      openErrorWindow();
    }
  };

  // handle media delete
  const onDeleteClick = (id) => {
    setConfirmDeleteMedia(id);
  };

  const closeDeleteModal = () => {
    setConfirmDeleteMedia(null);
  };

  const DELETE_MEDIA_FILE = gql`
    mutation DeleteMediaFile($id: ID!) {
      deleteMediaFile(id: $id) {
        resultCode
      }
    }
  `;

  const [deleteMediaFile] = useMutation(DELETE_MEDIA_FILE);

  const onConfirmDelete = async () => {
    try {
      if (deleteLoading) return;

      setDeleteLoading(true);
      setErrorMessage(null);

      const deleteResult = await deleteMediaFile({
        variables: {
          id: confirmDeleteMedia,
        },
      });

      setDeleteLoading(false);

      if (deleteResult.data.deleteMediaFile.resultCode === 5) {
        // media used in post, can't delete
        setErrorMessage(
          "The video is being used in a post and cannot be deleted. Please delete the post or remove the video from it first."
        );
        openErrorWindow();
        closeDeleteModal();
      }

      if (deleteResult.data.deleteMediaFile.resultCode !== 0) {
        openErrorWindow();
      }

      refetch();
    } catch (error) {
      captureException(error);
      console.log("onMediaDelete error", error);
      openErrorWindow();
    }
  };

  //console.log("data", data);

  return (
    <>
      {!data && (
        <LoadingBox>
          <BoxLoadingSpinner height="230px" />
        </LoadingBox>
      )}
      {data && (
        <Media
          data={data}
          warnings={warnings}
          fileInputRef={fileInputRef}
          fileInputKey={fileInputKey}
          showUploadButton={showUploadButton}
          onUploadClick={onUploadClick}
          uploadLoading={uploadLoading}
          uploadProgress={uploadProgress}
          onFileInputChange={onFileInputChange}
          onDeleteClick={onDeleteClick}
          closeDeleteModal={closeDeleteModal}
          confirmDeleteMedia={confirmDeleteMedia}
          onConfirmDelete={onConfirmDelete}
          uploadErrorMessage={uploadErrorMessage}
        />
      )}
      {errorWindow && (
        <ModalWrap>
          <Backdrop
            onClick={handleModalClose}
            parameters="error"
            background="rgba(0, 0, 0, 0.7)"
          />
          <MessageWindow handleModalClose={handleModalClose} type="error">
            {errorMessage}
          </MessageWindow>
        </ModalWrap>
      )}
    </>
  );
};
